import { defineComponent as _defineComponent } from 'vue'
import CollapsableCard from "@/components/bootstrap/CollapsableCard.vue";
import Toggle from "@/components/bootstrap/Toggle.vue";
import {useTranslations} from "@/hooks/useTranslations";
import {debounce} from "@/utils/Helpers";
import {Allergens} from "@/models/NutritionLabels/Allergens";
import {computed, inject, ref, watch} from "vue";
import {splitLanguages} from "@/models/NutritionLabels/v2/Helpers";
import {LabelLanguage} from "@/models/NutritionLabels/LabelLanguage";

type AllergensConfigurationProps = {
  value: Allergens[],
  language: keyof typeof LabelLanguage,
  mayContainText: Partial<Record<LabelLanguage, string[]>>
}


export default /*#__PURE__*/_defineComponent({
  __name: 'AllergensConfiguration',
  props: {
    value: null,
    language: null,
    mayContainText: null
  },
  emits: ["fetch-preview", "update:value", "update:mayContainText"],
  setup(__props: any, { emit }) {

const props = __props as {
  value: Allergens[],
  language: keyof typeof LabelLanguage,
  mayContainText: Partial<Record<LabelLanguage, string[]>>
};

const fetchPreview: () => void = inject("bopFetchPreview");



const translations = useTranslations();
const iterableAllergens = computed(() => Object.keys(Allergens).filter((key) => isNaN(Number(key))));
const languageHeaders = computed(() => {
  return splitLanguages(LabelLanguage[props.language]).map((l) => translations.value.get(LabelLanguage[l]));
});

const model = ref(props.value.map((a) => a.toString()));
const localMayContain = ref<Partial<Record<LabelLanguage, string[]>>>({});
const languages = computed(() =>
  splitLanguages(LabelLanguage[props.language]) as LabelLanguage[]
);

watch(
  () => props.value,
  (newVal) => {
    model.value = newVal.map((a) => a.toString());
  },
  { deep: true, immediate: true }
);

watch(
  () => props.mayContainText,
  (val) => {
    localMayContain.value = { ...val };
  },
  { deep: true, immediate: true }
);

watch(
  languages,
  (activeLanguages) => {
    const cleaned: Partial<Record<LabelLanguage, string[]>> = {};

    for (const lang of activeLanguages) {
      if (localMayContain.value[lang]) {
        cleaned[lang] = [...localMayContain.value[lang]!];
      }
    }

    localMayContain.value = cleaned;

    // Notify parent immediately so backend data stays clean
    emit("update:mayContainText", cleaned);
  },
  { immediate: true }
);

const mayContainError = computed(() => {
  if (languages.value.length < 2) return null;

  const [l1, l2] = languages.value;
  const a = localMayContain.value[l1] ?? [];
  const b = localMayContain.value[l2] ?? [];

  return a.length !== b.length
    ? translations.value.get("error_missing_translation")
    : null;
});

function onAllergenToggled(allergen: string, isChecked: boolean) {
  if (isChecked) {
    model.value.push(allergen);
  }

  else {
    model.value = model.value.filter((x) => x != allergen);
  }

  emit('update:value', model.value.map((a) => Allergens[a]));
  debouncedFetchPreview();
}

function onTagAdded() {
  emit("update:mayContainText",{ ...localMayContain.value });

  if (!mayContainError.value) {
    debouncedFetchPreview();
  }
}

function debouncedFetchPreview() {
  debounce(fetchPreview, 1000, "back-of-package-config-label-update");
}

return { __sfc: true,fetchPreview, props, emit, translations, iterableAllergens, languageHeaders, model, localMayContain, languages, mayContainError, onAllergenToggled, onTagAdded, debouncedFetchPreview, CollapsableCard, Toggle, Allergens, LabelLanguage }
}

})